import React from 'react';
import { Icon } from "@iconify/react";
import { useSubscriptionRenewal } from '../../../context/SubscriptionRenewalContext.jsx';
import '../../../styles/Auth/profile.css';

const getRemainingDaysIST = (finalExpiryDate) => {
    if (!finalExpiryDate) return null;
    const options = { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' };
    const nowISTStr = new Date().toLocaleDateString('en-CA', options);
    const expiryISTStr = new Date(finalExpiryDate).toLocaleDateString('en-CA', options);
    const [nY, nM, nD] = nowISTStr.split('-').map(Number);
    const [eY, eM, eD] = expiryISTStr.split('-').map(Number);
    const nowMs = Date.UTC(nY, nM - 1, nD);
    const expMs = Date.UTC(eY, eM - 1, eD);
    return Math.round((expMs - nowMs) / (1000 * 60 * 60 * 24));
};

const getDynamicSubscriptionStatus = (status, finalExpiryDate) => {
    if (status === 'Expired') {
        return { text: 'EXPIRED', className: 'expired' };
    }
    if (status === 'Renewed' || status === 'Cancelled') {
        return { text: status.toUpperCase(), className: status.toLowerCase() };
    }
    if (!finalExpiryDate) {
        return { text: status?.toUpperCase() || 'UNKNOWN', className: status?.toLowerCase() || '' };
    }

    const remainingDays = getRemainingDaysIST(finalExpiryDate);

    if (remainingDays !== null && remainingDays <= 0) {
        return { text: 'EXPIRED', className: 'expired' };
    } else if (remainingDays === 1) {
        return { text: 'EXPIRES TOMORROW', className: 'expires-tomorrow' };
    } else if (remainingDays <= 7) {
        return { text: `EXPIRING IN ${remainingDays} DAYS`, className: 'expiring-7' };
    } else if (remainingDays <= 15) {
        return { text: `EXPIRING IN ${remainingDays} DAYS`, className: 'expiring-15' };
    } else {
        return { text: 'ACTIVE', className: 'active' };
    }
};

const ProfileSubscriptionCard = () => {
    const { subscriptionData } = useSubscriptionRenewal();

    if (!subscriptionData) return null;

    return (
        <div className="profile-subscription-card">
            <div className="profile-sub-header">
                <div className="profile-sub-title">
                    <Icon icon="solar:card-search-bold-duotone" className="profile-sub-icon" />
                    <div>
                        <h4>Subscription Details</h4>
                        <p>Complete active package & validity overview</p>
                    </div>
                </div>
            </div>

            <div className="profile-sub-grid">
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Current Package Name</span>
                    <span className="profile-sub-value pkg-badge">{subscriptionData.packageName || 'N/A'}</span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Package Price</span>
                    <span className="profile-sub-value">₹{(subscriptionData.packagePrice || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">GST Percentage</span>
                    <span className="profile-sub-value">{subscriptionData.gstPercentage || 0}%</span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">GST Type</span>
                    <span className="profile-sub-value">{subscriptionData.gstType || 'Exclusive'}</span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">GST Amount</span>
                    <span className="profile-sub-value">
                        ₹{(() => {
                            let base = subscriptionData.packagePrice || 0;
                            let gst = subscriptionData.gstAmount;
                            if (gst === undefined) {
                                if (subscriptionData.isGstApplicable && subscriptionData.gstType === 'Exclusive') {
                                    gst = (base * (subscriptionData.gstPercentage || 0)) / 100;
                                } else if (subscriptionData.isGstApplicable && subscriptionData.gstType === 'Inclusive') {
                                    gst = base - (base / (1 + (subscriptionData.gstPercentage || 0) / 100));
                                } else {
                                    gst = 0;
                                }
                            }
                            return (gst || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                        })()}
                    </span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Total Amount</span>
                    <span className="profile-sub-value total-highlight">
                        ₹{(() => {
                            let base = subscriptionData.packagePrice || 0;
                            let gst = subscriptionData.gstAmount;
                            let total = subscriptionData.totalAmount;
                            if (total === undefined) {
                                if (subscriptionData.isGstApplicable && subscriptionData.gstType === 'Exclusive') {
                                    gst = (base * (subscriptionData.gstPercentage || 0)) / 100;
                                    total = base + gst;
                                } else {
                                    total = base;
                                }
                            }
                            return (total || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                        })()}
                    </span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Subscription Start Date</span>
                    <span className="profile-sub-value">
                        {subscriptionData.validityStartDate
                            ? new Date(subscriptionData.validityStartDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                            : 'N/A'}
                    </span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Base Validity</span>
                    <span className="profile-sub-value">{subscriptionData.baseValidity} {subscriptionData.validityUnit}</span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Extra Validity / Offer</span>
                    <span className="profile-sub-value">
                        {subscriptionData.extraValidity > 0
                            ? `${subscriptionData.extraValidity} ${subscriptionData.extraValidityUnit || 'Days'}`
                            : 'None'}
                    </span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Base Expiry Date</span>
                    <span className="profile-sub-value">
                        {subscriptionData.baseExpiryDate
                            ? new Date(subscriptionData.baseExpiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                            : 'N/A'}
                    </span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Final Expiry Date</span>
                    <span className="profile-sub-value expiry-highlight">
                        {subscriptionData.finalExpiryDate
                            ? new Date(subscriptionData.finalExpiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                            : 'N/A'}
                    </span>
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Subscription Status</span>
                    {(() => {
                        const { text, className } = getDynamicSubscriptionStatus(subscriptionData.subscriptionStatus, subscriptionData.finalExpiryDate);
                        return (
                            <span className={`sub-status-pill ${className}`}>
                                {text}
                            </span>
                        );
                    })()}
                </div>
                <div className="profile-sub-item">
                    <span className="profile-sub-label">Remaining Days</span>
                    <span className="profile-sub-value">
                        {(() => {
                            const days = getRemainingDaysIST(subscriptionData.finalExpiryDate) ?? subscriptionData.remainingDays;
                            if (days === null) return 'N/A';
                            return days < 0 ? '0 Days (Expired)' : `${days} Days`;
                        })()}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ProfileSubscriptionCard;
